import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as MdIcons from 'react-icons/md';

const { MdCloudUpload, MdAdd, MdHistory, MdWarning, MdMusicNote } = MdIcons;

const AudioUploader = ({ onFileUpload, audioFile }) => {
  const fileInputRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Automatically generate a playable URL when audioFile changes
  useEffect(() => {
    if (audioFile && !audioFile.fromHistory) {
      const url = URL.createObjectURL(audioFile);
      setAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setAudioUrl(null);
    }
  }, [audioFile]);

  const validateAndUpload = (file) => {
    if (file && file.type.startsWith('audio/')) {
      if (file.size > 12 * 1024 * 1024) {
        alert("Warning: This file is over 12MB. It may fail to upload due to API limits. Try a smaller file or use the Mixer to compress it.");
      }
      onFileUpload(file);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    validateAndUpload(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    validateAndUpload(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isLargeFile = audioFile && !audioFile.fromHistory && audioFile.size > 10 * 1024 * 1024;

  return (
    <div className="flex flex-col p-1 rounded-2xl bg-gradient-to-b from-slate-200 to-slate-100 dark:from-border-dark/50 dark:to-background-dark">
      <motion.div 
        onClick={() => fileInputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        whileHover={{ scale: 1.005 }}
        whileTap={{ scale: 0.995 }}
        className="bg-white dark:bg-surface-dark rounded-[14px] p-8 md:p-12 flex flex-col items-center justify-center text-center gap-6 border-2 border-dashed border-slate-300 dark:border-border-dark hover:border-primary dark:hover:border-primary transition-all duration-300 group cursor-pointer relative overflow-hidden"
      >
        <input 
          ref={fileInputRef} 
          type="file" 
          accept="audio/*" 
          onChange={handleFileChange} 
          className="hidden" 
        />
        
        {/* Decor BG */}
        <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
        
        {audioFile ? (
          <div className="z-10 flex flex-col items-center w-full">
            <div className="relative w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              {audioFile.fromHistory ? (
                <SafeIcon icon={MdHistory} className="text-5xl text-primary" />
              ) : (
                <SafeIcon icon={MdMusicNote} className="text-5xl text-primary" />
              )}
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-4 border-white dark:border-surface-dark">
                <span className="text-white text-xs font-bold">✓</span>
              </div>
            </div>
            
            <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white w-full max-w-md truncate px-4 mb-2">
              {audioFile.name}
            </h3>
            
            <div className="flex flex-col gap-1 items-center mb-4">
              <p className="text-slate-500 dark:text-text-secondary">
                {audioFile.fromHistory ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span className="w-2 h-2 rounded-full bg-orange-400"></span>
                    Loaded from History
                  </span>
                ) : (
                  <>
                    {formatFileSize(audioFile.size)} • Ready
                  </>
                )}
              </p>
              {isLargeFile && (
                <span className="flex items-center gap-1.5 text-xs font-bold text-amber-500 bg-amber-500/10 px-3 py-1 rounded-full mt-2">
                  <SafeIcon icon={MdWarning} />
                  Heavy file ({formatFileSize(audioFile.size)})
                </span>
              )}
            </div>

            {audioUrl && (
              <div 
                className="w-full max-w-md mt-2 mb-4" 
                onClick={(e) => e.stopPropagation()} // Prevent triggering file upload when interacting with player
              >
                <audio controls src={audioUrl} className="w-full h-12 outline-none rounded-full" />
              </div>
            )}

            <p className="text-primary text-sm font-bold uppercase tracking-wider mt-2">
              Click to replace
            </p>
          </div>
        ) : (
          <>
            <div className="relative w-24 h-24 rounded-full bg-slate-100 dark:bg-background-dark flex items-center justify-center mb-2 group-hover:scale-110 transition-transform duration-300 shadow-xl">
              <SafeIcon icon={MdCloudUpload} className="text-5xl text-primary" />
              <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-primary rounded-full flex items-center justify-center border-4 border-white dark:border-surface-dark">
                <SafeIcon icon={MdAdd} className="text-white text-sm" />
              </div>
            </div>
            <div className="flex flex-col gap-3 max-w-md z-10">
              <h3 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">Upload Audio Track</h3>
              <p className="text-slate-500 dark:text-text-secondary">
                Drop your audio file here or click to browse.
                <br className="hidden sm:block"/>
                <span className="text-xs opacity-70">(Max recommended size: 10MB)</span>
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 z-10">
              {['MP3', 'WAV', 'FLAC'].map((fmt) => (
                <span key={fmt} className="px-3 py-1 rounded-md bg-slate-100 dark:bg-background-dark text-xs font-bold text-slate-500 dark:text-text-secondary border border-slate-200 dark:border-border-dark">
                  {fmt}
                </span>
              ))}
            </div>
            <button className="mt-4 px-8 py-3 bg-slate-900 dark:bg-white text-white dark:text-black font-bold rounded-full hover:scale-105 transition-transform shadow-lg z-10">
              Browse Files
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AudioUploader;